import { processBackOrForward } from "./modules/processBackOrForward";
import { processHistoryApiBasedRouting } from "./modules/processHistoryApiBasedRouting";
import { processNavigate } from "./modules/processNavigate";
import { processReload } from "./modules/processReload";
import { processUnknown } from "./modules/processUnknown";
import { FScriptHistoryState } from "./types/FScriptHistoryState";
import { HistoryStackEntry } from "./types/HistoryStackEntry";
import { NavigationEventCallback } from "./types/NavigationEventCallback";
import { NavigationEventType } from "./types/NavigationEventType";
import { PageEnterDetail } from "./types/PageEnterDetail";
import { logger } from "./logger";

/**
 * Class for tracking and reporting page navigation metrics
 */
export class NavigationTracker {
  static SESSION_KEY = "fscrtip_session";
  private _sessionId: string = "";

  get sessionId(): string {
    if (!this._sessionId) {
      const sessionStorageSession = sessionStorage.getItem(
        NavigationTracker.SESSION_KEY
      );

      if (sessionStorageSession) {
        this._sessionId = sessionStorageSession;
      } else {
        // If no session storage key is set, use the default
        this._sessionId = Date.now().toString();
        sessionStorage.setItem(NavigationTracker.SESSION_KEY, this._sessionId);
      }

      logger.debug("Using storage key: " + this._sessionId);
    }

    return this._sessionId;
  }

  private static readonly HISTORY_STACK_KEY = "customHistoryStack";
  private static readonly HISTORY_INDEX_KEY = "customHistoryIndex"; // Index of the *previous* page in the stack

  public triggeredEvents: Map<NavigationEventType, boolean> = new Map();

  private get callbacks(): NavigationEventCallback[] {
    logger.debug("Accessing callbacks array");

    if (
      typeof window !== "undefined" &&
      (window as any)["__fscript_callbacks"] instanceof Array
    ) {
      logger.debug("Returning existing callbacks array");
      return (window as any)["__fscript_callbacks"];
    }

    logger.debug("Creating new callbacks array");
    return ((window as any)["__fscript_callbacks"] = []);
  }

  private processHistoryApiBasedRoutingTimer: NodeJS.Timeout | undefined =
    undefined;

  /**
   * Creates a new NavigationMetrics instance
   * @param callback Function that will be called for all navigation events
   */
  constructor(
    callback: NavigationEventCallback,
    logLevel: "debug" | null = "debug"
  ) {
    logger.debug("NavigationTracker initialized with log level: " + logLevel);

    this.callbacks.push(callback);
    this.initialize();
  }

  /**
   * Add a callback function to be called for navigation events
   * @param callback Function that will be called for navigation events
   */
  public addCallback(callback: NavigationEventCallback): void {
    logger.debug("Adding callback for navigation events");

    this.callbacks.push(callback);
  }

  /**
   * Remove a callback function
   * @param callback Function to remove
   * @returns true if callback was found and removed, false otherwise
   */
  public removeCallback(callback: NavigationEventCallback): boolean {
    logger.debug("Removing callback for navigation events");

    const index = this.callbacks.indexOf(callback);
    if (index !== -1) {
      this.callbacks.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Initialize page navigation tracking
   */
  private initialize = () => {
    logger.debug("Initializing NavigationTracker");

    // Ensure current page has a unique fscriptId
    this.ensureHistoryStateId();
    this.processStatic();

    this.createHistoryProxy();

    window.addEventListener("popstate", this.popStateEventHandler);

    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        // Send another pageview if the page is restored from bfcache.
        logger.debug("Page restored from bfcache, emitting event");

        // this.emitEvent("back_or_forward_navigation", "cache");

        this.processHistoryApiBasedRoutingTimer = processHistoryApiBasedRouting(
          {
            emitEvent: this.emitEvent,
            // fscriptId: event.state?.fscriptId,
            fscriptId: history.state?.__PRIVATE_NEXTJS_INTERNALS_TREE?.[5],
            HistoryIndexKey: NavigationTracker.HISTORY_INDEX_KEY,
            historyStack: this.historyStack,
            HistoryStackKey: NavigationTracker.HISTORY_STACK_KEY,
            lastKnownIndex: this.historyIndex,
            fscriptSessionId: this.sessionId,
          }
        );
      }
    });
  };

  popStateEventHandler = (event: PopStateEvent) => {
    logger.debug("Handling popstate event");

    clearInterval(this.processHistoryApiBasedRoutingTimer);

    this.processHistoryApiBasedRoutingTimer = processHistoryApiBasedRouting({
      emitEvent: this.emitEvent,
      // fscriptId: event.state?.fscriptId,
      fscriptId: event.state?.__PRIVATE_NEXTJS_INTERNALS_TREE?.[5],
      HistoryIndexKey: NavigationTracker.HISTORY_INDEX_KEY,
      historyStack: this.historyStack,
      HistoryStackKey: NavigationTracker.HISTORY_STACK_KEY,
      lastKnownIndex: this.historyIndex,
      fscriptSessionId: this.sessionId,
    });
  };

  createHistoryProxy = () => {
    logger.debug("Creating history proxy");

    window.history.pushState = new Proxy(window.history.pushState, {
      apply: (
        target,
        thisArg,
        argArray: [
          data: any,
          unused: string,
          url?: string | URL | null | undefined
        ]
      ) => {
        logger.debug("Intercepting pushState call");

        // Store previous pages in session storage instead of global scope
        const prevPage = localStorage.getItem(
          sessionStorage.getItem("fscrtip_session") + "__last_known_loaded_page"
        );

        if (prevPage !== location.href) {
          logger.debug(`Storing previous page: ${prevPage}`, { prevPage });

          localStorage.setItem(
            sessionStorage.getItem("fscrtip_session") +
              "__page_before_last_known_loaded",
            prevPage || ""
          );
        } else {
          logger.debug(
            `Previous page is the same as current: ${prevPage}, skipping storage`,
            { prevPage }
          );
        }

        logger.debug(`Storing current page: ${location.href}`);

        localStorage.setItem(
          sessionStorage.getItem("fscrtip_session") +
            "__last_known_loaded_page",
          location.href
        );

        logger.debug(`Previous page stored: ${location.href}`, { prevPage });

        const nextFScriptId = this.buildFScriptId();

        const [data, , url] = argArray;
        const finalState = data
          ? {
              ...data,
              fscriptId: nextFScriptId,
              ...(data.__PRIVATE_NEXTJS_INTERNALS_TREE
                ? {
                    __PRIVATE_NEXTJS_INTERNALS_TREE: [
                      ...data.__PRIVATE_NEXTJS_INTERNALS_TREE,
                      nextFScriptId,
                    ],
                  }
                : {}),
            }
          : { fscriptId: nextFScriptId };

        processNavigate({
          emitEvent: this.emitEvent,
          fscriptId: nextFScriptId,
          HistoryIndexKey: NavigationTracker.HISTORY_INDEX_KEY,
          historyStack: this.historyStack,
          HistoryStackKey: NavigationTracker.HISTORY_STACK_KEY,
          lastKnownIndex: this.historyIndex,
          fscriptSessionId: this.sessionId,
        });

        return target.call(thisArg, finalState, "", url);
      },
    });
  };

  private buildFScriptId = () =>
    `fscript_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

  /**
   * Ensure the current history state has a fscriptId
   */
  private ensureHistoryStateId(): string {
    logger.debug("Ensuring history state ID");

    const currentState = window.history.state || {};

    if (currentState.fscriptId) {
      return currentState.fscriptId;
    }

    const newId = this.buildFScriptId();

    const newState: FScriptHistoryState = {
      ...currentState,
      fscriptId: newId,
    };

    window.history.replaceState(newState, "");

    return newId;
  }

  /**
   * Get current history state ID or generate a new one
   */
  private getCurrentHistoryStateId(): string {
    logger.debug("Getting current history state ID");

    if (window.history.state && window.history.state.fscriptId) {
      return window.history.state.fscriptId;
    }

    return this.ensureHistoryStateId();
  }

  /**
   * Get navigation type from the Performance API
   */
  get navigationType() {
    const navEntries = performance.getEntriesByType(
      "navigation"
    ) as PerformanceNavigationTiming[];

    return navEntries.length > 0 ? navEntries[0].type : "navigate";
  }

  get historyStack() {
    return JSON.parse(
      localStorage.getItem(
        NavigationTracker.HISTORY_STACK_KEY + this.sessionId
      ) || "[]"
    ) as Array<HistoryStackEntry>;
  }

  get historyIndex() {
    return JSON.parse(
      localStorage.getItem(
        NavigationTracker.HISTORY_INDEX_KEY + this.sessionId
      ) || "-1"
    ) as number;
  }

  get currentFscriptId() {
    return this.getCurrentHistoryStateId();
  }

  get currentBrowserUrl() {
    return window.location.href;
  }

  private processStatic = (location?: string) => {
    logger.debug("Processing static navigation");

    const navigationType = this.navigationType;

    const processArgs = {
      currentUrl: location || this.currentBrowserUrl,
      emitEvent: this.emitEvent,
      fscriptId: this.currentFscriptId,
      HistoryIndexKey: NavigationTracker.HISTORY_INDEX_KEY,
      historyStack: this.historyStack,
      HistoryStackKey: NavigationTracker.HISTORY_STACK_KEY,
      lastKnownIndex: this.historyIndex,
      fscriptSessionId: this.sessionId,
    };

    if (navigationType === "navigate") {
      logger.debug("Processing navigate event");

      processNavigate(processArgs);

      return;
    }

    if (navigationType === "reload") {
      logger.debug("Processing reload event");

      processReload(processArgs);

      return;
    }

    if (navigationType === "back_forward") {
      logger.debug("Processing back/forward event");

      processBackOrForward(processArgs);

      return;
    }

    processUnknown(processArgs);
  };

  /**
   * Emit a navigation event to all callbacks
   * @param type Type of navigation event
   * @param detail Optional detail for page_load events
   */
  private emitEvent = (type: NavigationEventType, detail?: PageEnterDetail) => {
    logger.debug(`Emitting event: ${type}`);

    const event = {
      type,
      url: window.location.href,
      detail,
    };

    if (type === "browser_back") {
    }

    // if (type === "browser_back" || type === "browser_forward")

    // if (type === "page_load" && !this.triggeredEvents.has("page_load")) {
    if (type === "page_load") {
      logger.debug("Processing page load event");

      if (
        true
        // !this.triggeredEvents.has("page_load") &&
        // !this.triggeredEvents.has("browser_back") &&
        // !this.triggeredEvents.has("browser_forward")
      ) {
        const prevPage = localStorage.getItem(
          sessionStorage.getItem("fscrtip_session") + "__last_known_loaded_page"
        );

        if (prevPage !== location.href) {
          logger.debug(`Storing previous page: ${prevPage}`, { prevPage });

          localStorage.setItem(
            sessionStorage.getItem("fscrtip_session") +
              "__page_before_last_known_loaded",
            prevPage || ""
          );
        } else {
          logger.debug(
            `Previous page is the same as current: ${prevPage}, skipping storage`,
            { prevPage }
          );
        }

        logger.debug(`Storing current page: ${location.href}`);

        localStorage.setItem(
          sessionStorage.getItem("fscrtip_session") +
            "__last_known_loaded_page",
          location.href
        );
      }
    }

    this.triggeredEvents.set(type, true);

    this.callbacks.forEach((callback) => callback(event));

    const loadTriggerRequired =
      type === "browser_back" ||
      type === "browser_forward" ||
      type === "page_reload";

    if (loadTriggerRequired) {
      logger.debug("Triggering page load event for history navigation");
      this.emitEvent("page_load");
      // Store previous pages in session storage instead of global scope
    }
  };
}
